// src/pages/Dashboard/StatsBox.js
import React from "react";
import useChatbotStats from "./useChatbotStats";
import {
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  useTheme,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faLink,
  faFont,
  faComment,
} from "@fortawesome/free-solid-svg-icons";

export default function StatsBox() {
  const { stats, error, loading } = useChatbotStats();
  const theme = useTheme();

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }} color="text.primary">
          Loading stats...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Paper sx={{ p: 2, bgcolor: "background.paper" }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );

  if (!stats)
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.primary">
          No stats available for this chatbot.
        </Typography>
      </Paper>
    );

  const cardStyle = {
    p: 3,
    borderRadius: 3,
    bgcolor: "background.paper",
    boxShadow: theme.palette.mode === "dark" ? 4 : 2,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  };

  return (
    <Box sx={{ flexGrow: 1, mt: 3 }}>
      <Grid container spacing={3}>
        {/* Number of Files */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={cardStyle}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.primary">
                Number of Files
              </Typography>
              <FontAwesomeIcon icon={faFileAlt} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }} color="text.primary">
              {stats.privateDocs.total_documents}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`${stats.privateDocs.total_page_characters} chars`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`${stats.privateDocs.total_page_words} words`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`${stats.privateDocs.total_tokens} tokens`}
              />
            </Typography>
          </Paper>
        </Grid>

        {/* Website Links */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={cardStyle}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.primary">
                No. of Website Links
              </Typography>
              <FontAwesomeIcon icon={faLink} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }} color="text.primary">
              {stats.websiteLinks.total_links}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`${stats.websiteLinks.total_page_characters} chars`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`${stats.websiteLinks.total_page_words} words`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`${stats.websiteLinks.total_tokens} tokens`}
              />
            </Typography>
          </Paper>
        </Grid>

        {/* Total Words */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={cardStyle}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.primary">
                Total Words
              </Typography>
              <FontAwesomeIcon icon={faFont} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }} color="text.primary">
              {stats.totalWords}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Chars: ${stats.totalCharacters}`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`Tokens: ${stats.totalTokens}`}
              />
            </Typography>
          </Paper>
        </Grid>

        {/* Used Tokens */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={cardStyle}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.primary">
                Chatbot Used Tokens
              </Typography>
              <FontAwesomeIcon icon={faComment} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: "bold" }} color="text.primary">
              {stats.tokens.total_total_token}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Input: ${stats.tokens.total_input_tokens}`}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                label={`Output: ${stats.tokens.total_output_tokens}`}
              />
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
